import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { useI18n } from '@/shared/i18n';
import { Button } from './Button';
import classes from './FileUploadDropzone.module.css';

export type FileUploadDropzoneHandle = {
  open: () => void;
};

export type FileUploadDropzoneLabels = {
  idle?: string;
  active?: string;
  uploading?: string;
  formats?: string;
  selectFile?: string;
  rejected?: string;
  dropRelease?: string;
};

export type FileUploadDropzoneTestIds = {
  root?: string;
  input?: string;
  overlay?: string;
};

export type FileUploadDropzoneProps = {
  onFileSelected: (file: File) => void;
  isUploading?: boolean;
  disabled?: boolean;
  variant?: 'large' | 'upload';
  accept?: Accept;
  maxSize?: number;
  maxFiles?: number;
  noClick?: boolean;
  showSelectButton?: boolean;
  showPageOverlay?: boolean;
  labels?: FileUploadDropzoneLabels;
  testIds?: FileUploadDropzoneTestIds;
  onDebugEvent?: (message: string) => void;
  children?: ReactNode;
};

export const FileUploadDropzone = forwardRef<FileUploadDropzoneHandle, FileUploadDropzoneProps>(
  function FileUploadDropzone(
    {
      onFileSelected,
      isUploading = false,
      disabled = false,
      variant = 'large',
      accept,
      maxSize,
      maxFiles = 1,
      noClick = false,
      showSelectButton = true,
      showPageOverlay = false,
      labels,
      testIds,
      onDebugEvent,
      children,
    },
    ref,
  ) {
    const { t } = useI18n();
    const isDisabled = disabled || isUploading;

    const handleAccepted = useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) {
        onDebugEvent?.('drop_without_file');
        return;
      }
      onDebugEvent?.(`file_accepted:${file.name}`);
      onFileSelected(file);
    }, [onDebugEvent, onFileSelected]);

    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragReject,
      open,
    } = useDropzone({
      onDropAccepted: handleAccepted,
      onDropRejected: (rejections) => {
        const reason = rejections[0]?.errors[0]?.code ?? 'unknown';
        onDebugEvent?.(`drop_rejected:${reason}`);
      },
      accept,
      maxSize,
      maxFiles,
      multiple: maxFiles > 1,
      disabled: isDisabled,
      noClick,
      noKeyboard: isDisabled,
      preventDropOnDocument: true,
      onDragEnter: () => onDebugEvent?.('dragenter'),
      onDragOver: () => onDebugEvent?.('dragover'),
      onDragLeave: () => onDebugEvent?.('dragleave'),
      onFileDialogOpen: () => onDebugEvent?.('click_open_picker'),
      onFileDialogCancel: () => onDebugEvent?.('picker_closed_without_file'),
    });

    useImperativeHandle(ref, () => ({ open }), [open]);

    const title = isUploading
      ? (labels?.uploading ?? t('components.fileUpload.uploading'))
      : isDragActive
        ? (labels?.active ?? t('components.fileUpload.dropHere'))
        : (labels?.idle ?? t('components.fileUpload.dragOrClick'));

    const rootClassName = [
      classes.root,
      variant === 'upload' ? classes.upload : classes.large,
      isDragActive ? classes.active : '',
    ].filter(Boolean).join(' ');

    return (
      <section className={classes.shell}>
        {showPageOverlay && isDragActive && !isUploading ? (
          <div
            className={classes.overlay}
            data-testid={testIds?.overlay ?? 'file-upload-page-drop-overlay'}
          >
            {labels?.dropRelease ?? t('components.fileUpload.dropRelease')}
          </div>
        ) : null}
        <div
          {...getRootProps({ className: rootClassName })}
          data-testid={testIds?.root ?? 'file-upload-dropzone'}
        >
          <input {...getInputProps()} data-testid={testIds?.input ?? 'file-upload-input'} />
          <strong>{title}</strong>
          {isDragReject ? (
            <span className={classes.warning}>
              {labels?.rejected ?? t('components.fileUpload.rejected')}
            </span>
          ) : null}
          <span>{labels?.formats ?? t('components.fileUpload.formats')}</span>
          {showSelectButton ? (
            <Button
              type="button"
              variant="secondary"
              disabled={isDisabled}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onDebugEvent?.('click_open_picker_button');
                open();
              }}
            >
              {labels?.selectFile ?? t('components.fileUpload.selectFile')}
            </Button>
          ) : null}
          {children}
        </div>
      </section>
    );
  },
);
